import React, { useState, Fragment, useMemo, useEffect } from 'react';

import { ArrowTopRightOnSquareIcon, CheckCircleIcon, RocketLaunchIcon, XCircleIcon } from '@heroicons/react/20/solid';
import Button from './Button';
import { type IMetricEvent, sendEvent } from '../../utilities/analytics';
import NewHomepageButtonGroup from './NewHomepageButtonGroup';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { HomeIcon } from '@heroicons/react/24/outline';
import { getConfig } from '../../config/config-helper';
import type { ISolutionDeploymentDetail } from '../../interfaces/ISolutionDeployment';

interface INewHomepageLaunchButtonProps {
  deployment: ISolutionDeploymentDetail;
  projectPath: string;
  size?: 'medium' | 'large';
  primaryButton?: boolean;
  buttonText?: string;
}

type PipelineStatus = 'SETUP_SUCCESS' | 'SETUP_IN_PROGRESS' | 'SETUP_FAILED';

export default function NewHomepageLaunchButton({
  deployment,
  projectPath,
  size = 'medium',
  primaryButton = true,
  buttonText = 'Open homepage',
}: INewHomepageLaunchButtonProps) {
  const salesForceId = deployment.extra_env_vars?.DATAOPS_CATALOG_OPPORTUNITY_ID ?? '';
  const solutionId = deployment.extra_env_vars?.DATAOPS_CATALOG_SOLUTION_TEMPLATE_ID ?? '';
  const { username } = useCurrentUser();
  const [salesForceIdValue, setSalesForceIdValue] = useState<string>(salesForceId);
  const [showDemoPurpose, setShowDemoPurpose] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [deploymentHomepageUrl, setDeploymentHomepageUrl] = useState<string | undefined>();
  const { dataopsliveBaseUrl } = getConfig();

  // Get the latest pipeline run
  const latestPipelineRun = useMemo(() => {
    if (!deployment.pipeline_runs || deployment.pipeline_runs.length === 0) {
      return undefined;
    }
    // Sort by created_at descending to get the latest
    return [...deployment.pipeline_runs].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )[0];
  }, [deployment.pipeline_runs]);

  // Determine pipeline status based on latest pipeline run
  const pipelineStatus = useMemo((): PipelineStatus => {
    if (!latestPipelineRun) {
      return 'SETUP_IN_PROGRESS';
    }

    switch (latestPipelineRun.status) {
      case 'success':
        return 'SETUP_SUCCESS';
      case 'failed':
      case 'canceled':
        return 'SETUP_FAILED';
      default:
        return 'SETUP_IN_PROGRESS';
    }
  }, [latestPipelineRun]);

  // Solution homepage URL effect
  useEffect(() => {
    if (latestPipelineRun?.homepage_job_id) {
      // Construct the homepage URL using the project_id and homepage_job_id
      const pipelineId = String(latestPipelineRun.pipeline_id);
      const jobId = String(latestPipelineRun.homepage_job_id);

      setDeploymentHomepageUrl(`${dataopsliveBaseUrl}/${projectPath}/documentations/${pipelineId}?job=${jobId}`);
    } else {
      setDeploymentHomepageUrl(undefined);
    }
  }, [latestPipelineRun, deployment.project_id, dataopsliveBaseUrl]);

  const onClose = () => {
    setOpen(false);
    setShowDemoPurpose(false);
  };

  const onNavigateToSolutionHomepage = (demoPurpose: 'Personal' | 'Customer' | 'LargeAudience') => {
    onClose();

    const eventPayload: IMetricEvent = {
      name: 'deployment-homepage-opened',
      properties: {
        username,
        deploymentName: `${deployment.solution_prefix}-${deployment.id}`,
        solutionProjectId: solutionId,
        demoPurpose,
        salesForceId: salesForceIdValue,
        workflow: 'new',
      },
    };
    sendEvent(eventPayload);

    window.open(deploymentHomepageUrl, '_blank')?.focus();
  };

  return (
    <>
      <Transition show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-30"
          onClose={() => {
            onClose();
          }}
        >
          <TransitionChild
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 z-10">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <TransitionChild
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel className="p-6 relative transform rounded-lg bg-white text-left shadow-xl transition-all my-8">
                  <div>
                    {pipelineStatus === 'SETUP_SUCCESS' && (
                      <div>
                        <div className="mx-auto flex items-center space-x-2">
                          <div className="flex flex-row">
                            <CheckCircleIcon className="w-6 h-6 text-lime-600" />
                          </div>
                          <DialogTitle className="flex text-base justify-center">Deployment homepage ready</DialogTitle>
                        </div>
                        <NewHomepageButtonGroup
                          deployment={deployment}
                          onNavigateToSolutionHomepage={onNavigateToSolutionHomepage}
                          salesForceId={salesForceId}
                          setSalesForceId={setSalesForceIdValue}
                          onBack={onClose}
                        />
                      </div>
                    )}
                    {pipelineStatus === 'SETUP_IN_PROGRESS' && (
                      <>
                        <div className="mx-auto flex items-center space-x-2">
                          <div className="flex flex-row">
                            <RocketLaunchIcon className="w-6 h-6 text-sky-600" />
                          </div>
                          <DialogTitle className="flex text-base justify-center">Setup in progress</DialogTitle>
                        </div>

                        {!showDemoPurpose && (
                          <>
                            <div className="mt-3 sm:mt-5">
                              <div className="mt-3">
                                <p className="text-sm text-gray-500">
                                  Your deployment setup is in progress. You can see your homepage early but the
                                  vignettes and demo&apos;s should not be run until the setup is complete.
                                </p>
                                <p className="mt-3 text-sm text-gray-500">
                                  This dialog will auto-update and show you when setup is complete.
                                </p>
                              </div>
                            </div>
                            <ShowDemoPurposeButtons onClose={onClose} setShowDemoPurpose={setShowDemoPurpose} />
                          </>
                        )}
                        {showDemoPurpose && (
                          <NewHomepageButtonGroup
                            deployment={deployment}
                            onNavigateToSolutionHomepage={onNavigateToSolutionHomepage}
                            salesForceId={salesForceId}
                            setSalesForceId={setSalesForceIdValue}
                            onBack={() => {
                              setShowDemoPurpose(false);
                            }}
                          />
                        )}
                      </>
                    )}
                    {pipelineStatus === 'SETUP_FAILED' && (
                      <>
                        <div className="mx-auto flex items-center space-x-2">
                          <div className="flex flex-row">
                            <XCircleIcon className="w-6 h-6 text-dataops-secondary-maroon" />
                          </div>
                          <DialogTitle className="flex text-base justify-center">Setup issue</DialogTitle>
                        </div>
                        {!showDemoPurpose && (
                          <>
                            <ShowDemoPurposeButtons onClose={onClose} setShowDemoPurpose={setShowDemoPurpose} />
                          </>
                        )}
                        {showDemoPurpose && (
                          <NewHomepageButtonGroup
                            deployment={deployment}
                            onNavigateToSolutionHomepage={onNavigateToSolutionHomepage}
                            setSalesForceId={setSalesForceIdValue}
                            salesForceId={salesForceId}
                            onBack={() => {
                              setShowDemoPurpose(false);
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
      <div className="flex flex-col">
        <Button
          intent={primaryButton ? 'primary' : 'light'}
          size={size}
          className="z-10"
          onClick={() => {
            setOpen(true);
          }}
          disabled={deploymentHomepageUrl === undefined}
        >
          <HomeIcon className="w-4 h-4 mr-1" />
          {buttonText}
        </Button>
      </div>
    </>
  );
}

interface ShowDemoPurposeButtonsProps {
  onClose: () => void;
  setShowDemoPurpose: (value: boolean) => void;
}

function ShowDemoPurposeButtons({ onClose, setShowDemoPurpose }: ShowDemoPurposeButtonsProps) {
  return (
    <div className="mt-4 space-y-2">
      <Button
        size="large"
        intent="light"
        className="w-full justify-center"
        onClick={() => {
          setShowDemoPurpose(true);
        }}
      >
        Continue anyway
        <ArrowTopRightOnSquareIcon className="w-3 h-3 ml-1" />
      </Button>
      <Button
        size="large"
        intent="subtle"
        className="w-full justify-center"
        onClick={() => {
          onClose();
        }}
      >
        Back
      </Button>
    </div>
  );
}
